import type { ErrorEvent, Event } from "@sentry/browser";

let sentryReady: Promise<void> | undefined;
let sendException: ((error: unknown) => void) | undefined;

export type WebMonitoringConfiguration = {
  application: "hq" | "school";
  dsn?: string;
  environment: string;
  release?: string;
};

function sanitizeStackPath(value: string | undefined) {
  if (!value) return value;
  try {
    const url = new URL(value);
    return `${url.origin}${url.pathname}`;
  } catch {
    return value
      .replace(/\/Users\/[^/]+\//g, "/Users/[redacted]/")
      .replace(/\\Users\\[^\\]+\\/g, "\\Users\\[redacted]\\");
  }
}

function redactException(event: Event) {
  if (!event.exception?.values) return event.exception;
  return {
    values: event.exception.values.map((exception) => ({
      type: exception.type ?? "ApplicationError",
      value: "Error details redacted",
      module: exception.module,
      stacktrace: exception.stacktrace
        ? {
            frames_omitted: exception.stacktrace.frames_omitted,
            frames: exception.stacktrace.frames?.map((frame) => ({
              filename: sanitizeStackPath(frame.filename),
              function: frame.function,
              module: frame.module,
              platform: frame.platform,
              lineno: frame.lineno,
              colno: frame.colno,
              abs_path: sanitizeStackPath(frame.abs_path),
              in_app: frame.in_app,
              instruction_addr: frame.instruction_addr,
              addr_mode: frame.addr_mode,
              debug_id: frame.debug_id,
            })),
          }
        : undefined,
    })),
  };
}

/**
 * Keep only data required to group and symbolicate an application error.
 * Student/admin content, identity, navigation and request data are discarded.
 */
export function redactWebEvent(event: ErrorEvent): ErrorEvent {
  return {
    event_id: event.event_id,
    timestamp: event.timestamp,
    level: event.level,
    platform: event.platform,
    release: event.release,
    dist: event.dist,
    environment: event.environment,
    sdk: event.sdk,
    modules: event.modules,
    exception: redactException(event),
    debug_meta: event.debug_meta,
    tags: event.tags?.application
      ? { application: event.tags.application }
      : undefined,
    type: undefined,
  };
}

export function initializeWebMonitoring(
  configuration: WebMonitoringConfiguration,
) {
  const dsn = configuration.dsn?.trim();
  if (!dsn) return false;

  sentryReady = import("@sentry/browser").then(({ captureException, init }) => {
    init({
      dsn,
      enabled: true,
      environment: configuration.environment,
      release: configuration.release?.trim() || undefined,
      sendDefaultPii: false,
      enableLogs: false,
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      sendClientReports: false,
      beforeBreadcrumb: () => null,
      beforeSend: redactWebEvent,
      beforeSendTransaction: () => null,
      beforeSendLog: () => null,
      beforeSendMetric: () => null,
      integrations: (defaults) =>
        defaults.filter(
          (integration) =>
            integration.name !== "Breadcrumbs" &&
            integration.name !== "BrowserSession",
        ),
      initialScope: { tags: { application: configuration.application } },
    });
    sendException = (error) => {
      captureException(error);
    };
  });
  return true;
}

export function captureReactError(error: unknown) {
  if (sendException) {
    sendException(error);
    return;
  }
  void sentryReady?.then(() => sendException?.(error));
}
